'use client'

import { IMediaItem } from '@/lib/types/media/core'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
// HeroUI v3 removed Image component


interface UploadState {
  isUploading: boolean
  isProcessing: boolean
  progress: number
  error?: string | null
  previewUrl?: string
}

interface MediaThumbnailProps {
  media: IMediaItem & { previewUrl?: string }
  isSelected?: boolean
  onSelect?: () => void
  uploadState?: UploadState
  className?: string
}

export function MediaThumbnail({
  media,
  isSelected,
  onSelect,
  uploadState,
  className
}: MediaThumbnailProps) {
  // Calculate progress circle parameters
  const radius = 16; // Smaller radius for thumbnails
  const circumference = 2 * Math.PI * radius;
  const isUploading = uploadState?.isUploading ?? false;
  const isProcessing = uploadState?.isProcessing ?? false;
  const progress = uploadState?.progress ?? 0;
  const error = uploadState?.error;
  const progressOffset = circumference * (1 - (progress / 100));
  const isComplete = progress === 100;

  // For uploading files, prioritize the preview URL from the upload state
  const displayUrl = isUploading
    ? (media.previewUrl || uploadState?.previewUrl || media.url)
    : media.url;

  return (
    <div
      className={cn(
        'relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer',
        isSelected ? 'border-primary' : 'border-white/20 hover:border-white/60',
        className
      )}
      onClick={onSelect}
    >
      <div className={cn('relative w-full h-full', (isUploading || isProcessing) && 'animate-pulse')}>
        {/* Background Preview Container */}
        <div className="absolute inset-0">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={media.metadata.alt || ''}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-default-200" />
          )}
        </div>

        {/* Loading Overlay */}
        {(isUploading || isProcessing) && (
          <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px]">
            <div className="absolute inset-0 flex items-center justify-center">
              {isUploading && !isComplete && (
                <div className="relative flex flex-col items-center gap-1">
                  {/* Circular Progress */}
                  <div className="relative w-12 h-12">
                    <svg className="w-full h-full transform -rotate-90">
                      {/* Background circle */}
                      <circle
                        cx="24"
                        cy="24"
                        r={radius}
                        className="stroke-white/20"
                        strokeWidth="3"
                        fill="none"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="24"
                        cy="24"
                        r={radius}
                        className="stroke-primary"
                        strokeWidth="3"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={progressOffset}
                        style={{
                          transition: 'stroke-dashoffset 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      />
                    </svg>
                    {/* Centered percentage */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-semibold text-white">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {isProcessing && (
                <Icon icon='spinner' className="w-6 h-6 text-white animate-spin" />
              )}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isUploading && !isProcessing && (
          <div className="absolute inset-0 bg-danger/30 backdrop-blur-[2px] flex items-center justify-center">
            <Icon icon='alert-triangle' className="w-6 h-6 text-danger" />
          </div>
        )}
      </div>
    </div>
  );
}
